import { PRIMARY_USE_VALUES, PROJECT_STAGE_VALUES } from './entities/project.js';
import {
  SEED_CRITERION_IDS,
  SEED_SIZE_FLOOR_KINDS,
  SEED_STAKEHOLDER_TYPES,
  type ScoreCriterion,
  type ScoreImpact,
  type ScoreTemplateKey,
  type SeedSizeFloorKind,
  TEMPLATE_DEFAULTS,
  criterionLabel,
} from './scoring.js';
import { whereFieldName } from './where/describe.js';
import { type WhereRule, isWhereGroup } from './where/types.js';

/** Every criterion id a seed writes, by score; any other criterion is replaced without a trace. */
export const SEED_WRITTEN_IDS: Record<ScoreTemplateKey, readonly string[]> = {
  atlas: [
    SEED_CRITERION_IDS.market,
    SEED_CRITERION_IDS.sector,
    SEED_CRITERION_IDS.stage,
    SEED_CRITERION_IDS.size,
    'active',
    'developer_tier',
    'recent_signal',
  ],
  icp: [SEED_CRITERION_IDS.stakeholder, 'in_market', 'in_sector', 'recent_signal'],
  prospect: ['persona', 'account_tier_1', 'account_tier_2', 'email'],
};

type ListAnswer = 'markets' | 'sectors' | 'stages' | 'stakeholders';
/** Where each list answer lives once seeded, and the values the seed takes for it. */
const LIST_ANSWERS: Record<
  ListAnswer,
  {
    score: ScoreTemplateKey;
    id: string;
    key: string;
    impact: ScoreImpact;
    allowed?: readonly string[];
  }
> = {
  markets: { score: 'atlas', id: SEED_CRITERION_IDS.market, key: 'city', impact: 'very_important' },
  sectors: {
    score: 'atlas',
    id: SEED_CRITERION_IDS.sector,
    key: 'use',
    impact: 'very_important',
    allowed: PRIMARY_USE_VALUES,
  },
  stages: {
    score: 'atlas',
    id: SEED_CRITERION_IDS.stage,
    key: 'stage',
    impact: 'important',
    allowed: PROJECT_STAGE_VALUES,
  },
  stakeholders: {
    score: 'icp',
    id: SEED_CRITERION_IDS.stakeholder,
    key: 'type',
    impact: 'very_important',
    allowed: SEED_STAKEHOLDER_TYPES,
  },
};

export interface SeedReadBack {
  /** The answers the criteria still encode as seeded; an absent key could not be read back. */
  answers: Partial<Record<ListAnswer, string[]>> & {
    size_floor?: { kind: SeedSizeFloorKind; min: number };
  };
  /** What a new seed would remove for good: criteria it does not write, edited ones, values it cannot take. */
  lost: string[];
}

/** The criterion's one top-level rule, or null when it holds more or a group. */
const onlyRule = (c: ScoreCriterion): WhereRule | null => {
  const [first] = c.where.rules;
  return c.where.rules.length === 1 && !c.where.not && first && !isWhereGroup(first) ? first : null;
};

/** The inverse of the seed: reads the answers back out of the three scores' criteria. */
export function seedAnswersFromCriteria(
  criteria: Partial<Record<ScoreTemplateKey, readonly ScoreCriterion[]>>,
): SeedReadBack {
  const answers: SeedReadBack['answers'] = {};
  const lost: string[] = [];
  for (const score of ['atlas', 'icp', 'prospect'] as const) {
    const written = new Set(SEED_WRITTEN_IDS[score]);
    for (const criterion of criteria[score] ?? []) {
      if (!written.has(criterion.id))
        lost.push(criterionLabel(TEMPLATE_DEFAULTS[score].entity, criterion));
    }
  }
  for (const [answer, where] of Object.entries(LIST_ANSWERS) as [
    ListAnswer,
    (typeof LIST_ANSWERS)[ListAnswer],
  ][]) {
    const criterion = criteria[where.score]?.find((c) => c.id === where.id);
    if (!criterion) continue;
    const r = onlyRule(criterion);
    // A seeded list reads by its own name, else its field's: "Use: bogus", never the whole rule.
    const name =
      criterion.label?.trim() ||
      (r
        ? whereFieldName(TEMPLATE_DEFAULTS[where.score].entity, r.field)
        : criterionLabel(TEMPLATE_DEFAULTS[where.score].entity, criterion));
    const seeded =
      r !== null &&
      r.field === where.key &&
      r.operator === 'in' &&
      Array.isArray(r.value) &&
      criterion.impact === where.impact;
    if (!seeded) {
      lost.push(name);
      continue;
    }
    const values = (r.value as unknown[]).filter((v): v is string => typeof v === 'string');
    const allowed = where.allowed ? new Set<string>(where.allowed) : null;
    const kept = allowed ? values.filter((v) => allowed.has(v)) : values;
    const dropped = values.filter((v) => !kept.includes(v));
    if (dropped.length > 0) lost.push(`${name}: ${dropped.join(', ')}`);
    answers[answer] = kept;
  }
  const size = criteria.atlas?.find((c) => c.id === SEED_CRITERION_IDS.size);
  if (size) {
    const r = onlyRule(size);
    const min = typeof r?.value === 'number' ? r.value : 0;
    const seeded =
      r !== null &&
      (SEED_SIZE_FLOOR_KINDS as readonly string[]).includes(r.field) &&
      r.operator === '>' &&
      min > 0 &&
      size.impact === 'important';
    if (seeded && r) answers.size_floor = { kind: r.field as SeedSizeFloorKind, min };
    else lost.push(criterionLabel('project', size));
  }
  return { answers, lost };
}
